import Fa from 'admin/components/atoms/FontAwesome';
import { COLOR } from 'admin/constants/styles';
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// API リクエスト後の結果フィードバックを画面上部に固定表示するトースト。
// - 画面のどこにスクロールしていても上部に出るため見落としにくい。
// - 既定 3 秒で自動的に消え、× ボタンで即時に閉じることもできる。
// 結果通知の共通仕様として各画面で使い回すことを想定した共有部品。

export type NotificationVariant = 'success' | 'error';

type Props = {
  isOpen: boolean;
  // 表示するメッセージ。自動クローズのタイマーは message の参照変化で張り直すため、
  // 呼び出し側は文字列など安定した値を渡すこと（毎レンダー生成される JSX を渡すと
  // タイマーが張り直され続けて自動で閉じなくなる）。
  message: React.ReactNode;
  variant?: NotificationVariant;
  onClose: () => void;
  // 自動で閉じるまでのミリ秒。0 以下で自動クローズを無効化する。
  // 未指定時は variant で決まる（success=3000ms / error=0=手動クローズのみ）。
  // エラーは文面をコピーして後続対応に使うことがあるため既定で消さない。
  autoHideMs?: number;
};

const VARIANT = {
  success: { bg: COLOR.success, fg: '#1b5e20', border: COLOR.corporatePrimaryColor, icon: 'check-circle' },
  error: { bg: COLOR.alert, fg: COLOR.errorMessage, border: COLOR.errorMessage, icon: 'exclamation-circle' },
} as const;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -12px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

const Toast = styled.div<{ $variant: NotificationVariant }>`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100; /* reactstrap の Modal(1050) より前面に出す */
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 280px;
  max-width: min(92vw, 560px);
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ $variant }) => VARIANT[$variant].bg};
  color: ${({ $variant }) => VARIANT[$variant].fg};
  border: 1px solid ${({ $variant }) => VARIANT[$variant].border};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  animation: ${slideDown} 0.18s ease-out;
`;

const Text = styled.span`
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
`;

const CloseButton = styled.button`
  flex: none;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 2px;
  font-size: 15px;
  line-height: 1;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const NotificationToast = ({ isOpen, message, variant = 'success', onClose, autoHideMs }: Props) => {
  // 未指定なら variant で既定を決める（success=3000ms / error=手動クローズのみ）。
  const effectiveAutoHideMs = autoHideMs ?? (variant === 'error' ? 0 : 3000);

  // onClose の参照が毎レンダーで変わってもタイマーを張り直さないよう ref に退避する。
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // 開いている間だけ自動クローズのタイマーを張る。新しい通知（message/variant 変化）で張り直す。
  useEffect(() => {
    if (!isOpen || effectiveAutoHideMs <= 0) return undefined;
    const timer = setTimeout(() => onCloseRef.current(), effectiveAutoHideMs);
    return () => clearTimeout(timer);
  }, [isOpen, effectiveAutoHideMs, message, variant]);

  if (!isOpen) return null;

  return (
    <Toast
      $variant={variant}
      // 成功は割り込まない status(polite)、エラーは即読み上げの alert(assertive)。
      role={variant === 'error' ? 'alert' : 'status'}
    >
      <Fa icon={VARIANT[variant].icon} />
      <Text>{message}</Text>
      <CloseButton type="button" aria-label="閉じる" onClick={onClose}>
        <Fa icon="times" />
      </CloseButton>
    </Toast>
  );
};

export default NotificationToast;
